import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Button, Empty, Input, Popconfirm, Select, Spin, Switch, Table, Tag,
  Tooltip, message,
} from "antd";
import { DeleteOutlined, KeyOutlined, LeftOutlined } from "@ant-design/icons";

import { controller } from "../../controller";
import { getCarrierCredentialCoverage } from "./controller";
import PortalCredentialsPanel from "../Office/PortalCredentials/PortalCredentialsPanel";
import {
  DEFAULT_COVERAGE_CATEGORIES,
  formatCodesMultiline,
  parseProcedureCodeTextarea,
  mapToRows,
  rowsToMap,
  CategoryMapEditor,
} from "../OfficeInsuranceCarriers/procedureCodeEditors";

/**
 * The rest of a carrier, in the same drawer as its playbook.
 *
 * Three things about a carrier used to live in three places: the playbook here,
 * the portal logins on each office's own settings page, and the procedure-code
 * defaults on a separate super-admin page most people never found. Answering
 * "can we actually do Sun Life for this practice" meant visiting all three.
 *
 * Nothing here reimplements what those pages did. The credentials tab embeds the
 * real PortalCredentialsPanel; the procedure-code editors are the same components
 * the old page used. Only the arrangement is new.
 */

/**
 * Render a dropdown next to its input instead of portalling it to document.body.
 *
 * REQUIRED for every Select inside the Drawer. antd puts popups on the body by
 * default, which lands them behind the Drawer's own stacking context — the
 * control looks fine and simply never opens. The same fix already appears on the
 * date pickers in PolicyCriticalFields and the carrier picker in
 * PolicyReviewChecklist; it is a property of Drawer, not of any one field.
 */
export const popupInDrawer = (trigger) => trigger.parentElement;

// Matches PORTAL_META in PortalCredentialsPanel. One login serves every carrier
// in the group, which is why the credentials tab talks about portals and not
// about the carrier you clicked.
const PORTAL_LABEL = {
  SUNLIFE: "Sun Life portal",
  PROVIDER_AGGREGATOR: "Provider Connect",
};

const CONTACT_METHODS = [
  { value: "", label: "Unknown" },
  { value: "portal", label: "On portal" },
  { value: "call", label: "By phone" },
  { value: "call_email", label: "By phone & email" },
];

function when(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch (e) {
    return "—";
  }
}

function httpFailed(res) {
  return res && res._httpStatus != null && res._httpStatus >= 400;
}

function detailOf(res, fallback) {
  if (res && typeof res.detail === "string") return res.detail;
  if (res && res.raw && typeof res.raw.detail === "string") return res.raw.detail;
  return fallback;
}

// --- credentials -------------------------------------------------------------

/**
 * Who can actually sign in to this carrier, per office.
 *
 * The table lists every office, not only the ones with a login — the gap is the
 * whole reason to look. "Manage" swaps in the office's real credentials panel,
 * scoped to this carrier's portal, so adding a login here is the same operation
 * (and the same validation) as adding it on the office's own page.
 */
export function CredentialsTab({ carrier, officeId, setOfficeId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async function () {
      setLoading(true);
      const res = await getCarrierCredentialCoverage(carrier.carrier_id);
      setData((res && res.json) || null);
      setLoading(false);
    },
    [carrier.carrier_id],
  );

  useEffect(function () { load(); }, [load]);

  if (loading) return <div className="ck-loading">Loading…</div>;
  if (!data) return <Empty description="Couldn't load credentials" />;

  const portalCode = data.portal_code;
  const focusCarrier = {
    id: carrier.carrier_id,
    code: carrier.carrier_code,
    name: carrier.carrier_name,
    portalCode: portalCode,
  };
  const offices = data.offices || [];
  const chosen = offices.find(function (o) { return String(o.office_id) === String(officeId); });

  const columns = [
    {
      title: "Office",
      key: "office",
      render: function (_, r) {
        return (
          <div>
            <div className="ck-carrier">{r.office_name}</div>
            {r.practice_name ? <div className="ck-muted">{r.practice_name}</div> : null}
          </div>
        );
      },
    },
    {
      title: "Logins",
      key: "accounts",
      width: "38%",
      render: function (_, r) {
        if (!r.accounts.length) return <span className="ck-blank">none</span>;
        return (
          <span className="ck-taskcell">
            {r.accounts.map(function (a) {
              return (
                <Tag key={a.id} className="ck-tag" color={a.is_default ? "purple" : undefined}>
                  {(a.provider && a.provider.name) || "Office account"}
                  {a.is_default ? " · default" : ""}
                </Tag>
              );
            })}
          </span>
        );
      },
    },
    {
      title: "Last verified",
      key: "verified",
      width: "18%",
      render: function (_, r) {
        const latest = r.accounts
          .map(function (a) { return a.last_verified_at; })
          .filter(Boolean)
          .sort()
          .pop();
        return <span className="ck-muted">{when(latest)}</span>;
      },
    },
    {
      title: "",
      key: "action",
      width: 96,
      align: "right",
      render: function (_, r) {
        return (
          <Button size="small" onClick={function () { setOfficeId(String(r.office_id)); }}>
            {r.has_login ? "Manage" : "Add"}
          </Button>
        );
      },
    },
  ];

  return (
    <div className="ck-creds">
      {portalCode ? (
        <Alert
          className="ck-alert"
          type="info"
          showIcon
          icon={<KeyOutlined />}
          message={`Signed in through ${PORTAL_LABEL[portalCode] || portalCode}`}
          description="One login covers every carrier on this portal, so the accounts below are shared with the carrier's portal siblings."
        />
      ) : (
        <Alert
          className="ck-alert"
          type="warning"
          showIcon
          message="No portal we fetch automatically"
          description="Anything saved here is a manual login your FTC team signs in with by hand."
        />
      )}

      {chosen ? (
        <div className="ck-creds-office">
          <div className="ck-creds-office-head">
            <Button size="small" icon={<LeftOutlined />} onClick={function () { setOfficeId(null); }}>
              All offices
            </Button>
            <span className="ck-carrier">{chosen.office_name}</span>
            {chosen.practice_name ? <span className="ck-muted">{chosen.practice_name}</span> : null}
          </div>
          {/* The office's own panel, verbatim. Create, edit, set-default, 2FA
              reset and the insurance-profile requirement all come along already
              built — and stay identical to what the office sees. */}
          <PortalCredentialsPanel
            embedded
            officeId={chosen.office_id}
            focusCarrier={focusCarrier}
            onChanged={load}
          />
        </div>
      ) : (
        <>
          <div className="ck-counts ck-creds-counts">
            <strong>{data.counts.with_login}</strong> of {data.counts.total} offices have a login
          </div>
          <Table
            rowKey="office_id"
            columns={columns}
            dataSource={offices}
            pagination={false}
            size="small"
            tableLayout="fixed"
          />
        </>
      )}
    </div>
  );
}

// --- procedure codes ----------------------------------------------------------

/**
 * Which codes a breakdown asks about, at both levels that exist.
 *
 * The two blocks are deliberately far apart and labelled by blast radius: the top
 * one changes every office at once, the bottom one changes exactly the office you
 * picked. Getting those the wrong way round is the mistake this layout is built
 * to prevent, so neither is ever on screen without its scope stated.
 */
export function ProcedureCodesTab({ carrier, officeId, setOfficeId, offices }) {
  const [loading, setLoading] = useState(true);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [savingOffice, setSavingOffice] = useState(false);
  const [globalCodes, setGlobalCodes] = useState("");
  const [globalRows, setGlobalRows] = useState([]);
  const [officeLoading, setOfficeLoading] = useState(false);
  const [officeCodes, setOfficeCodes] = useState("");
  const [officeRows, setOfficeRows] = useState([]);
  const [officeInherits, setOfficeInherits] = useState(true);
  const [categories, setCategories] = useState(DEFAULT_COVERAGE_CATEGORIES);

  const loadGlobal = useCallback(
    async function () {
      setLoading(true);
      const res = await controller.getInsuranceProvider(carrier.carrier_id);
      if (!httpFailed(res)) {
        setGlobalCodes(
          Array.isArray(res.default_breakdown_procedure_codes) && res.default_breakdown_procedure_codes.length
            ? formatCodesMultiline(res.default_breakdown_procedure_codes)
            : "",
        );
        setGlobalRows(mapToRows(res.default_procedure_code_categories || {}));
      }
      setLoading(false);
    },
    [carrier.carrier_id],
  );

  useEffect(function () { loadGlobal(); }, [loadGlobal]);

  const loadOffice = useCallback(
    async function () {
      if (!officeId) return;
      setOfficeLoading(true);
      const codesRes = await controller.getOfficeBreakdownProcedureCodes(officeId, carrier.carrier_id);
      if (!httpFailed(codesRes)) {
        // `override_codes` null means the office inherits. Show the inherited
        // list anyway — editing from a blank box would silently look like the
        // office had nothing.
        const overriding = codesRes.override_codes != null;
        setOfficeInherits(!overriding);
        setOfficeCodes(
          formatCodesMultiline(overriding ? codesRes.override_codes : codesRes.effective_codes),
        );
      }
      const catRes = await controller.getOfficeProcedureCategories(officeId, carrier.carrier_id);
      if (!httpFailed(catRes)) {
        setOfficeRows(mapToRows(catRes.override_map || {}));
        if (Array.isArray(catRes.valid_categories) && catRes.valid_categories.length) {
          setCategories(catRes.valid_categories);
        }
      }
      setOfficeLoading(false);
    },
    [officeId, carrier.carrier_id],
  );

  useEffect(function () { loadOffice(); }, [loadOffice]);

  async function saveGlobal() {
    setSavingGlobal(true);
    const codes = parseProcedureCodeTextarea(globalCodes);
    const map = rowsToMap(globalRows);
    const res = await controller.updateInsuranceProvider(carrier.carrier_id, {
      default_breakdown_procedure_codes: codes.length ? codes : null,
      default_procedure_code_categories: Object.keys(map).length ? map : null,
    });
    setSavingGlobal(false);
    if (httpFailed(res)) {
      message.error(detailOf(res, "Couldn't save the defaults."));
      return;
    }
    message.success("Defaults saved for every office.");
    loadGlobal();
  }

  async function saveOffice() {
    if (!officeId) return;
    setSavingOffice(true);
    const codesRes = await controller.setOfficeBreakdownProcedureCodes(
      officeId, carrier.carrier_id, parseProcedureCodeTextarea(officeCodes),
    );
    if (httpFailed(codesRes)) {
      setSavingOffice(false);
      message.error(detailOf(codesRes, "Couldn't save the override."));
      return;
    }
    const catRes = await controller.setOfficeProcedureCategories(
      officeId, carrier.carrier_id, rowsToMap(officeRows),
    );
    setSavingOffice(false);
    if (httpFailed(catRes)) {
      message.error(detailOf(catRes, "Codes saved, but the categories didn't."));
      return;
    }
    message.success("Override saved for this office only.");
    loadOffice();
  }

  // Every code this carrier is actually asked about here — the carrier-wide
  // default plus whatever the selected office overrides it with. Mapping a
  // category onto a code outside that set does nothing, so the picker offers
  // exactly this and no more. Derived from the textareas rather than the saved
  // values, so a code typed above is selectable below without a save first.
  const codeOptions = useMemo(
    function () {
      const seen = new Set();
      parseProcedureCodeTextarea(globalCodes).forEach(function (c) { seen.add(c); });
      parseProcedureCodeTextarea(officeCodes).forEach(function (c) { seen.add(c); });
      return Array.from(seen).sort(function (a, b) {
        return a.localeCompare(b, undefined, { numeric: true });
      });
    },
    [globalCodes, officeCodes],
  );

  if (loading) return <div className="ck-loading">Loading…</div>;

  return (
    <div className="ck-codes">
      <div className="ck-scope-block">
        <div className="ck-scope-head">
          <span className="ck-scope-title">Default for {carrier.carrier_name}</span>
          <Tag color="purple" className="ck-tag">every office</Tag>
        </div>
        <p className="ck-muted">
          The codes a breakdown asks this carrier about when an office hasn't said
          otherwise. One per line; empty clears the default.
        </p>
        <Input.TextArea
          className="ck-mono"
          value={globalCodes}
          onChange={function (e) { setGlobalCodes(e.target.value); }}
          autoSize={{ minRows: 5, maxRows: 16 }}
          placeholder="01204&#10;02144"
        />

        <label className="ck-label">Category for specific codes</label>
        <p className="ck-muted">
          {codeOptions.length
            ? "Pick from the codes above. Anything not listed falls back to the CDA code range."
            : "Add some codes above first — a category can only be set on a code this carrier is asked about."}
        </p>
        <CategoryMapEditor
          rows={globalRows}
          onChange={setGlobalRows}
          validCategories={categories}
          codeOptions={codeOptions}
          getPopupContainer={popupInDrawer}
        />

        <div className="ck-editor-foot">
          <span className="ck-muted">Applies wherever no office override exists.</span>
          <Button type="primary" loading={savingGlobal} onClick={saveGlobal}>
            Save default
          </Button>
        </div>
      </div>

      <div className="ck-scope-block ck-scope-block--office">
        <div className="ck-scope-head">
          <span className="ck-scope-title">Override for one office</span>
          <Tag className="ck-tag">that office only</Tag>
        </div>
        <Select
          showSearch
          allowClear
          optionFilterProp="label"
          placeholder="Pick an office"
          value={officeId ? String(officeId) : undefined}
          onChange={function (v) { setOfficeId(v || null); }}
          options={(offices || []).map(function (o) {
            return { value: String(o.id), label: o.practice ? `${o.name} — ${o.practice}` : o.name };
          })}
          style={{ width: "100%" }}
          getPopupContainer={popupInDrawer}
        />

        {!officeId ? (
          <p className="ck-muted ck-scope-hint">
            Pick an office to give it its own list. Until then every office uses
            the default above.
          </p>
        ) : (
          <Spin spinning={officeLoading}>
            {officeInherits ? (
              <p className="ck-muted ck-scope-hint">
                This office currently inherits the default. Saving below turns the
                list into an override.
              </p>
            ) : (
              <p className="ck-muted ck-scope-hint">
                This office overrides the default. Clear the box and save to go
                back to inheriting.
              </p>
            )}
            <Input.TextArea
              className="ck-mono"
              value={officeCodes}
              onChange={function (e) { setOfficeCodes(e.target.value); }}
              autoSize={{ minRows: 5, maxRows: 16 }}
              placeholder="One code per line"
            />

            <label className="ck-label">Category for specific codes — this office</label>
            <p className="ck-muted">
              Beats both the carrier default and the automatic category. Remove
              every row to drop the office override.
            </p>
            {!codeOptions.length ? (
              <p className="ck-muted">Add some codes first — the picker lists this carrier's codes.</p>
            ) : null}
            <CategoryMapEditor
              rows={officeRows}
              onChange={setOfficeRows}
              validCategories={categories}
              codeOptions={codeOptions}
              getPopupContainer={popupInDrawer}
            />

            <div className="ck-editor-foot">
              <span className="ck-muted">Nothing outside this office changes.</span>
              <Button type="primary" loading={savingOffice} onClick={saveOffice}>
                Save override
              </Button>
            </div>
          </Spin>
        )}
      </div>
    </div>
  );
}

// --- the carrier record --------------------------------------------------------

function prettyCapability(key) {
  return String(key)
    .split("_")
    .map(function (s) { return s.charAt(0).toUpperCase() + s.slice(1); })
    .join(" ");
}

function prettyMethod(key) {
  if (key === "scraper") return "Scraper";
  if (key === "llm_navigator_scraper") return "LLM Navigator";
  if (key === "ai_phone_call") return "AI Phone Call";
  if (key === "pdf_upload") return "PDF Upload";
  return key;
}

/**
 * The carrier record itself, plus what the platform can do with it.
 *
 * The editable half is the row in the database. The capability half below it is
 * hardcoded in carrier_capabilities.py and shown as reference — changing it is a
 * deploy, not a form, and the panel says so rather than offering inputs that
 * would quietly do nothing.
 */
export function CarrierRecordTab({ carrier, capabilities, onSaved, onDeleted }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ policy_count: 0, can_delete: false });

  const load = useCallback(
    async function () {
      setLoading(true);
      const res = await controller.getInsuranceProvider(carrier.carrier_id);
      if (!httpFailed(res)) {
        setForm({
          code: res.code || "",
          name: res.name || "",
          is_active: !!res.is_active,
          phone_number: res.phone_number || "",
          portal_name: res.portal_name || "",
          portal_url: res.portal_url || "",
          breakdown_method: res.breakdown_method || "",
          estimate_method: res.estimate_method || "",
        });
        setMeta({ policy_count: res.policy_count || 0, can_delete: !!res.can_delete });
      }
      setLoading(false);
    },
    [carrier.carrier_id],
  );

  useEffect(function () { load(); }, [load]);

  function set(field, value) {
    setForm(function (prev) { return Object.assign({}, prev, { [field]: value }); });
  }

  async function save() {
    if (!form.name.trim()) {
      message.error("A carrier needs a name.");
      return;
    }
    setSaving(true);
    const res = await controller.updateInsuranceProvider(carrier.carrier_id, {
      name: form.name.trim(),
      is_active: form.is_active,
      phone_number: form.phone_number.trim(),
      portal_name: form.portal_name.trim(),
      portal_url: form.portal_url.trim(),
      breakdown_method: form.breakdown_method,
      estimate_method: form.estimate_method,
    });
    setSaving(false);
    if (httpFailed(res)) {
      message.error(detailOf(res, "Couldn't save the carrier."));
      return;
    }
    message.success("Carrier saved.");
    load();
    if (onSaved) onSaved();
  }

  async function remove() {
    const res = await controller.deleteInsuranceProvider(carrier.carrier_id);
    if (httpFailed(res)) {
      message.error(detailOf(res, "Couldn't delete the carrier."));
      return;
    }
    message.success("Carrier deleted.");
    if (onDeleted) onDeleted();
  }

  if (loading || !form) return <div className="ck-loading">Loading…</div>;

  const entry = (capabilities && capabilities.carriers && capabilities.carriers[form.code])
    || (capabilities && capabilities.default)
    || {};
  const capKeys = (capabilities && capabilities.capabilities) || [];

  return (
    <div className="ck-editor ck-record">
      <label className="ck-label">Code</label>
      <Input value={form.code} disabled />
      <p className="ck-muted">
        Used by integrations and stored on every policy — it can't change after
        the carrier is created.
      </p>

      <label className="ck-label">Display name</label>
      <Input value={form.name} onChange={function (e) { set("name", e.target.value); }} />

      <div className="ck-record-toggle">
        <Switch checked={form.is_active} onChange={function (v) { set("is_active", v); }} />
        <span>{form.is_active ? "Active — offered when adding a policy" : "Inactive — hidden from new policies"}</span>
      </div>

      <label className="ck-label">Provider phone line</label>
      <Input
        value={form.phone_number}
        onChange={function (e) { set("phone_number", e.target.value); }}
        placeholder="1-800-…"
      />

      <label className="ck-label">Portal name</label>
      <Input
        value={form.portal_name}
        onChange={function (e) { set("portal_name", e.target.value); }}
        placeholder="e.g. Provider Connect"
      />

      <label className="ck-label">Portal URL</label>
      <Input
        value={form.portal_url}
        onChange={function (e) { set("portal_url", e.target.value); }}
        placeholder="https://…"
      />

      {/* These two drive the contact bar FTC staff read above the playbook, so
          they belong next to it rather than on a page of their own. */}
      <label className="ck-label">How breakdowns are obtained</label>
      <Select
        value={form.breakdown_method}
        onChange={function (v) { set("breakdown_method", v); }}
        options={CONTACT_METHODS}
        style={{ width: "100%" }}
        getPopupContainer={popupInDrawer}
      />

      <label className="ck-label">How estimates are obtained</label>
      <Select
        value={form.estimate_method}
        onChange={function (v) { set("estimate_method", v); }}
        options={CONTACT_METHODS}
        style={{ width: "100%" }}
        getPopupContainer={popupInDrawer}
      />

      <div className="ck-editor-foot">
        <span className="ck-muted">
          {meta.policy_count} {meta.policy_count === 1 ? "policy" : "policies"} on this carrier
        </span>
        <Button type="primary" loading={saving} onClick={save}>Save carrier</Button>
      </div>

      <div className="ck-scope-block ck-scope-block--ref">
        <div className="ck-scope-head">
          <span className="ck-scope-title">What the platform can do</span>
          <Tag className="ck-tag">reference</Tag>
        </div>
        <p className="ck-muted">
          Hardcoded in <code>clinics/carrier_capabilities.py</code>. Changing it is
          a deploy, not a setting.
        </p>
        {capKeys.length ? (
          <div className="ck-caps">
            {capKeys.map(function (cap) {
              const methods = Array.isArray(entry[cap]) ? entry[cap] : [];
              return (
                <div className="ck-cap" key={cap}>
                  <span className="ck-cap-name">{prettyCapability(cap)}</span>
                  <span className="ck-cap-methods">
                    {methods.length
                      ? methods.map(function (m) {
                          return <Tag key={m} className="ck-tag">{prettyMethod(m)}</Tag>;
                        })
                      : <span className="ck-blank">nothing automated</span>}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <span className="ck-blank">Capability matrix unavailable.</span>
        )}
      </div>

      <div className="ck-danger">
        {meta.can_delete ? (
          <Popconfirm
            title="Delete this carrier?"
            description="Its playbooks and knowledge go with it."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={remove}
            getPopupContainer={popupInDrawer}
          >
            <Button danger icon={<DeleteOutlined />}>Delete carrier</Button>
          </Popconfirm>
        ) : (
          <Tooltip
            title={`${meta.policy_count} patient ${meta.policy_count === 1 ? "policy points" : "policies point"} at this carrier. Deactivate it instead.`}
            getPopupContainer={popupInDrawer}
          >
            <span>
              <Button danger icon={<DeleteOutlined />} disabled>Delete carrier</Button>
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
